import React from "react";
import { ApolloError } from "@apollo/client";
import { Navigate, useLocation } from "react-router-dom";
import { UserRole, hasRequiredRole } from "@modules/authentication/roles";
import { useCurrentUser } from "@modules/authentication/useCurrentUser";
import { Routes } from "~/routes";
import { useAuth } from "@components/auth-provider/AuthProvider";

export interface AuthenticatedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}
/**
 * This wrapper component ensures a page can only be viewed if the user is authenticated.
 */
const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = (props) => {
  const auth = useAuth();
  const location = useLocation();
  const { currentUser, loading, error } = useCurrentUser();

  // Avoid redirect loops: if the backend explicitly rejects auth, clear the token.
  React.useEffect(() => {
    if (!auth.loaded || !auth.isAuthenticated) {
      return;
    }
    if (loading) {
      return;
    }
    if (!error) {
      return;
    }

    const isUnauthorized =
      error instanceof ApolloError &&
      (error.graphQLErrors.some((gqlError) => {
        const code = (gqlError.extensions as { code?: string } | undefined)?.code;
        return (
          code === "UNAUTHENTICATED" ||
          code === "FORBIDDEN" ||
          code === "AUTH_NOT_AUTHORIZED" ||
          code === "AUTH_NOT_AUTHENTICATED"
        );
      }) ||
        ((error.networkError as any)?.statusCode ?? (error.networkError as any)?.response?.status) === 401 ||
        ((error.networkError as any)?.statusCode ?? (error.networkError as any)?.response?.status) === 403);

    if (isUnauthorized) {
      auth.setAuthToken(null);
    }
  }, [auth, error, loading]);

  if (!auth.loaded) {
    return <></>;
  }

  if (!auth.isAuthenticated) {
    return <Navigate to={Routes.Login} replace state={{ from: location }} />;
  }

  if (loading) {
    return <></>;
  }

  if (error && currentUser === undefined) {
    // Non-auth errors (network, CORS, backend down) are handled by showing an
    // inline error. Auth errors are already handled by the useEffect above which
    // clears the token → isAuthenticated becomes false → redirects via the
    // earlier !isAuthenticated check.
    return (
      <>
        <p>Something went wrong loading your session.</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </>
    );
  }

  if (!currentUser) {
    return <Navigate to={Routes.Login} replace state={{ from: location }} />;
  }

  if (
    props.allowedRoles &&
    !hasRequiredRole(currentUser.role, props.allowedRoles)
  ) {
    if (!currentUser.role) {
      // Role could not be determined (e.g. missing from JWT).
      // Don't redirect — it would loop since every route requires a role.
      return (
        <>
          <p>Your account does not have an assigned role.</p>
          <button onClick={() => {
        auth.logout();
      }}>Sign out</button>
        </>
      );
    }

    // Redirect normal users to AppNotAvailable, admins to Home
    if (currentUser.role === UserRole.User) {
      return <Navigate to={Routes.AppNotAvailable} replace />;
    }

    return <Navigate to={Routes.Home} replace />;
  }

  return <>{props.children}</>;
};

export default AuthenticatedRoute;
